import { useState } from 'react'
import { Alert, Form, Input, InputNumber, Modal, Typography } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { changeVispQuota, type Visp } from './networkApi'

interface FormValues {
  quota: number
  reason: string
}

const MAX_STEP = 10_000

export function VispQuotaModal({ visp, onClose }: { visp: Visp | null; onClose: () => void }) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const quota = Form.useWatch('quota', form)

  const allocated = visp?.accounts.allocated ?? 0
  const toCreate = typeof quota === 'number' ? Math.max(0, quota - allocated) : 0

  const submit = () => {
    if (!visp) return
    submitOnValid(form, async (values) => {
      setSaving(true)
      setError(null)
      try {
        await changeVispQuota(visp.id, values.quota, values.reason.trim())
        void queryClient.invalidateQueries({ queryKey: queryKeys.network.all })
        onClose()
      } catch (err) {
        setError(translateError(err, t))
      } finally {
        setSaving(false)
      }
    })
  }

  return (
    <Modal
      open={visp !== null}
      title={visp ? t('network.visps.changeQuotaTitle', { name: visp.name }) : ''}
      onCancel={onClose}
      onOk={submit}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      confirmLoading={saving}
      destroyOnHidden
      afterOpenChange={(visible) => {
        if (!visible || !visp) return
        setError(null)
        form.setFieldsValue({ quota: visp.quota ?? allocated, reason: '' })
      }}
    >
      {error ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} /> : null}
      <Typography.Paragraph type="secondary">
        {t('network.visps.changeQuotaHint', { allocated })}
      </Typography.Paragraph>
      <Form form={form} layout="vertical" disabled={saving}>
        <Form.Item
          name="quota"
          label={t('network.visps.quotaNew')}
          extra={t('network.visps.quotaToCreate', { count: toCreate })}
          rules={[
            {
              validator: (_, value: number | null | undefined) =>
                typeof value === 'number' && value >= Math.max(1, allocated) && value - allocated <= MAX_STEP
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('network.visps.quotaRange', { min: Math.max(1, allocated), step: MAX_STEP }))),
            },
          ]}
        >
          <InputNumber min={1} max={100000} precision={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="reason"
          label={t('network.visps.quotaReason')}
          rules={[{ required: true, whitespace: true, max: 500, message: t('network.visps.quotaReasonRequired') }]}
        >
          <Input.TextArea rows={3} maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
